// Classic Four Ways, as a strategy.
//
// The four tickets are the product, so this is a wrapper and nothing else: it
// hands the raw snapshot straight to `lib/tickets.js` with exactly the
// arguments `store.js` used to pass, and only stamps the strategy fields onto
// the result. Nothing in the tier search is reimplemented here — when the
// tiers change, they change in `lib/tickets.js`, and this file follows.

import { LONG_BAND, generateTickets, normaliseOdds } from '../../tickets.js';
// The one writer of a tier string in the app — see lib/tier.js. A strategy says
// what to pick; what the result is called is read off its chance.
import { tierFor } from '../../tier.js';
import { bookFair } from '../universe.js';

export const CLASSIC = {
  id: 'classic-four-ways',
  name: 'Classic Four Ways',
  // 2 SINCE THE DAY THE TIERS STOPPED REACHING. A version-1 ticket in History
  // was picked by the old search — a probability band per tier, coin flips and
  // underdogs bought to hit it — and a version-2 one is favourites only with
  // the risk in the leg count. Same strategy id, different product, so the
  // stamp on the ticket has to tell them apart. `lib/migrate.js` keeps
  // `strategyVersion: 1` on legacy rows for exactly this reason.
  version: 2,
  builtin: true,
  description: "Four tickets a day, low to outrageous, from one bookmaker's prices.",
  filters: { sports: [], markets: ['h2h'], priceBand: [1, 1000], daysToKickoff: [0, 0], rules: [] },
  probabilitySource: { model: 'none', params: {} },
  activeLeg: { minEdge: 0, minEv: 0, devig: 'multiplicative' },
  legRule: { type: 'tier', tiers: ['low', 'medium', 'high', 'outrageous'] },
  // THE TIER SEARCH'S ONE DIAL, and the only thing about Classic worth tuning.
  // `longShots` (0..MAX_LONG_SHOTS) is how many of every tier's legs come out
  // of `longBand` instead of the favourites pool — a swap, never an extra leg,
  // so the tier stays the leg count it is. Classic itself ships at zero:
  // favourites only, which is the product. A tuned copy turns it up and keeps
  // the tiers. See `pickLegs` in lib/tickets.js.
  params: { longShots: 0, longBand: [...LONG_BAND] },
  // The floor and ceiling across all four tiers: low's two legs and
  // outrageous's eight. The per-tier counts live in `TIERS`.
  combiner: { minLegs: 2, maxLegs: 8, tickets: 4, distinctEvents: true, excludeSGM: true },
  staking: { type: 'flat' },
  bookmakerRule: 'single',
  slots: 4,
};

/**
 * Which side of the fixture a legacy leg backed. `lib/tickets.js` only ever
 * wrote the punter's sentence ("Collingwood to win"), and settlement needs a
 * word it can compare to `result.winner`. The universe knows both team names
 * for the event, so it answers exactly; the printed "A v B" is the fallback
 * for a leg whose match is no longer in the universe.
 */
function sideOf(leg, byEvent) {
  const team = String(leg.selection ?? '').replace(/ to win$/, '');
  const match = byEvent.get(leg.eventId);
  if (match) {
    if (team === match.home?.name) return 'home';
    if (team === match.away?.name) return 'away';
  }
  const home = String(leg.match ?? '').split(' v ')[0];
  return home && team === home ? 'home' : 'away';
}

/**
 * The legacy engine, stamped. `ctx.bookmaker` is the Odds API key the prices
 * are read at; `ctx.bookmakerName` is the name printed on the ticket.
 *
 * `strategy` is Classic itself, or a tuned copy of it — any document whose
 * `legRule.type` is `tier`, which `runStrategy` sends here rather than through
 * the candidate engine. Only `params` changes what it picks; everything else a
 * tier fork carries (edge thresholds, combiner, the leg rule's own tier list)
 * is the editor's furniture and the tier search has never read it.
 */
export function runClassic(universe, ctx, strategy = CLASSIC) {
  const s = strategy ?? CLASSIC;
  const events = normaliseOdds(universe?.rawEvents ?? [], ctx.bookmaker, ctx.oddsFetchedAt);
  const byEvent = new Map();
  for (const match of universe?.matches?.values?.() ?? []) {
    byEvent.set(match.providerEventId ?? match.matchKey, match);
  }

  // A FORK'S TICKETS ARE NOT CLASSIC'S. `generateTickets` ids a ticket by the
  // day and the tier, which was unambiguous while one strategy in the app used
  // tiers; two would answer to `2026-09-12-low` at once, and the paper trail
  // keys a proposal by that id. The fork's id goes in the middle, the shape
  // `runStrategy` uses for every other strategy.
  const idFor = (multi) => (s.id === CLASSIC.id
    ? multi.id
    : `${multi.forDate}-${s.id}-${multi.id.slice(String(multi.forDate).length + 1)}`);

  const tickets = generateTickets({
    events,
    forDate: ctx.forDate,
    asOf: ctx.asOf,
    // The cutoff and the price stamp are two moments on a set dealt hours after
    // the board was fetched. `ctx.asOf` cuts; this is what the ticket prints.
    oddsFetchedAt: ctx.oddsFetchedAt ?? ctx.asOf,
    stake: ctx.stake,
    bookmaker: ctx.bookmakerName,
    tiers: ctx.tiersShown,
    timeZone: ctx.timeZone,
    sameDay: ctx.sameDay,
    seed: ctx.seed,
    longShots: s.params?.longShots ?? 0,
    longBand: s.params?.longBand ?? LONG_BAND,
  }).map(({ multi, legs }) => {
    const id = idFor(multi);
    // The honest number this ticket is worth: each leg's own book, devigged
    // (multiplicative — the same margin-off reading the ⓘ now quotes), then
    // multiplied across the ticket. Null the moment one leg's match or price
    // cannot be found, rather than silently pricing the rest of the ticket —
    // a fairProbability that quietly dropped a leg would overstate the odds.
    const mappedLegs = legs.map((leg) => {
      const side = sideOf(leg, byEvent);
      const match = byEvent.get(leg.eventId);
      const pFair = match ? bookFair(match, side, ctx.bookmaker, 'multiplicative') : null;
      return {
        ...leg,
        id: `${id}-${leg.eventId}`,
        multiId: id,
        side,
        pFair,
        // WHICH SPORT THE LEG IS, in the field every other printer writes. The
        // legacy engine only ever wrote `sportTitle` — the competition's name,
        // for the eye — and a sport ID is what the screens sort and mark by:
        // Today's sports/racing filter, and the glyph on the ticket's eyebrow.
        // Without it Classic's own tickets were the one kind of ticket on the
        // pad with no shape beside their name. Null where the match has gone,
        // which is the same "unknown" `sportsOf` already skips.
        sport: match?.sport ?? null,
      };
    });
    const pFairs = mappedLegs.map((leg) => leg.pFair);
    const fairProbability = mappedLegs.length && pFairs.every((p) => Number.isFinite(p))
      ? pFairs.reduce((acc, p) => acc * p, 1)
      : null;
    const ev = fairProbability !== null && Number.isFinite(multi.combinedOdds)
      ? fairProbability * multi.combinedOdds - 1
      : null;
    return {
      multi: {
        ...multi,
        id,
        strategyId: s.id,
        strategyVersion: s.version,
        label: s.name,
        fairProbability,
        ev,
        // THE RISK WORD COMES OFF THE DEVIGGED CHANCE, not off the implied one.
        //
        // `generateTickets` set the tier from `combinedProbability`, which for
        // Classic is the product of 1/price — the bookmaker's margin still in
        // it, on every leg, compounding. An eight-leg outrageous ticket is
        // flattered by a third of its own chance that way. `fairProbability`
        // above is the same legs with each book's margin taken off, and it is
        // what the ticket's own "Fair $4.40 · pays $4.12" line prints, so the
        // colour and the figures under it now read off one number. Null on a
        // ticket whose legs could not all be devigged, which keeps the word the
        // legacy engine wrote rather than inventing one.
        tier: tierFor(fairProbability ?? multi.combinedProbability),
      },
      legs: mappedLegs,
    };
  });
  return { tickets, candidates: [], notes: [] };
}
